import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, CommonModule } from '@angular/common';

import { Component, DOCUMENT, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '@ecs-environment';
import { LoadingLayerComponent } from '@ecs-features/loading-layer/loading-layer.component';
import { mediaQueries, mopinionScript, tracking } from '@ecs-helpers';
import { OverlayService } from '@ecs-services/overlay/overlay.service';
import { SearchStoreService } from '@ecs-services/search/search-store.service';
import { SEARCH_TERM_SEPARATOR } from '@ecs-services/search/search.helper';
import { SearchResultType } from '@ecs-services/search/search.interface';
import { TranslateService } from '@ngx-translate/core';
import {
    BaseComponent,
    HeaderConfigService,
    HeaderEventsDirective,
    HeaderLoginService,
    LanguageService,
    OsApiAuthService,
    SeoService,
    TrackingService,
    WINDOW,
    arrayEqualsShallow,
    getTranslationLanguageKey,
    getTwoLetterLanguageCode,
} from '@os-components';
import {
    SewWsContentWrapper,
    SewWsFloatingButtonsContactOptions,
    SewWsFooterApi,
    SewWsHeader,
} from '@sew/ui-components-angular/standalone';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { OverlayRightComponent } from './components/overlay-right/overlay-right.component';

@Component({
    selector: 'ecs-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        RouterOutlet,
        SewWsContentWrapper,
        SewWsFooterApi,
        SewWsHeader,
        SewWsFloatingButtonsContactOptions,
        LoadingLayerComponent,
        AsyncPipe,
        OverlayRightComponent,
        HeaderEventsDirective,
        CommonModule,
    ],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
    public useCMP = environment.useCMP;
    public hasSidePanel = false;
    public destroyed$ = new Subject<void>();
    public authConfig = environment.auth;
    public stage = environment.stage;

    public constructor(
        public sewConfigService: HeaderConfigService,
        public translateService: TranslateService,
        private auth: OsApiAuthService,
        private languageService: LanguageService,
        private trackingService: TrackingService,
        @Inject(WINDOW) private window: Window,
        @Inject(DOCUMENT) private document: Document,
        public overlayService: OverlayService,
        private breakpointObserver: BreakpointObserver,
        private seoService: SeoService,
        private router: Router,
        private searchStore: SearchStoreService,
        private headerLoginService: HeaderLoginService
    ) {
        super(sewConfigService, translateService, environment);
    }

    public ngOnInit() {
        this.trackingService.trackEvent(tracking.step.init);
        this.baseURL = environment.baseURLs.api.domain.length
            ? environment.baseURLs.api.domain
            : `${this.window.location.protocol}//${this.window.location.hostname}`;
        this.setSeoAttributes();
        this.caasBaseUrl = environment.caas.baseUrl;
        this.apiBaseUrl = environment.baseURLs.api.international;

        this.translateService.onLangChange.subscribe(() => {
            this.setFooterLinks();
        });

        this.languageObservable.subscribe((lang: string) => {
            this.setLanguage(lang);
        });

        this.headerLoginService.user
            .pipe(
                filter((user) => user !== undefined),
                switchMap(() => this.auth.fetchXCsrfToken())
            )
            .subscribe({
                error: (e) => {
                    console.error('Error while fetching x-csrf-token', e);
                },
            });

        if (environment.useMopinion) {
            const script = this.document.createElement('script');
            script.innerHTML = mopinionScript;
            this.document.body.appendChild(script);
        }

        this.breakpointObserver
            .observe(mediaQueries.mq35)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(({ matches }) => {
                this.hasSidePanel = matches;
            });

        this.trackSearchSuccess();
    }

    public ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private setLanguage(language: string) {
        const languageKey = getTranslationLanguageKey(language);
        this.language = language.toUpperCase();
        this.translateService.use(languageKey);
        this.languageService.language = languageKey;
        this.document.documentElement.lang = getTwoLetterLanguageCode(languageKey);
    }

    private setSeoAttributes() {
        const routerEvent = this.router.events;
        const searchResultObject = this.searchStore.getSearchResult().pipe(distinctUntilChanged());

        combineLatest([routerEvent, searchResultObject])
            .pipe(
                map(([route, searchResult]) => {
                    if (route instanceof NavigationEnd) {
                        switch (searchResult?.type) {
                            case SearchResultType.SERIALNUMBER:
                                return {
                                    textKeys: {
                                        title: 'SEO.SERIALNUMBER_PAGE.TITLE',
                                        description: 'SEO.SERIALNUMBER_PAGE.DESCRIPTION',
                                    },
                                    params: {
                                        catalogDescription: searchResult.catalogDescription,
                                        productName: searchResult.productName,
                                    },
                                };
                            case SearchResultType.SPARE_PARTS:
                                return {
                                    textKeys: {
                                        title: 'SEO.SPAREPART_PAGE.TITLE',
                                        description: 'SEO.SPAREPART_PAGE.DESCRIPTION',
                                    },
                                    params: {
                                        searchTerm: searchResult.spareParts
                                            ? searchResult.spareParts.join(SEARCH_TERM_SEPARATOR)
                                            : '',
                                    },
                                };

                            default:
                                return {
                                    textKeys: {
                                        title: 'SEO.STARTPAGE.TITLE',
                                        description: 'SEO.STARTPAGE.DESCRIPTION',
                                    },
                                    params: {},
                                };
                        }
                    } else {
                        return null;
                    }
                }),
                filter((seoData): seoData is Exclude<any, null> => seoData !== null)
            )
            .subscribe((seoData) => {
                this.translateService
                    .stream([seoData.textKeys.title, seoData.textKeys.description], { ...seoData.params })
                    .subscribe(
                        ({
                            [seoData.textKeys.title]: title,
                            [seoData.textKeys.description]: description,
                        }) => {
                            this.seoService.setMetaTitle(title);
                            this.seoService.setMetaDescription(description);
                        }
                    );
            });
    }

    private trackSearchSuccess() {
        this.searchStore
            .getSearchTermAndErrorsAndResult()
            .pipe(
                takeUntil(this.destroyed$),
                filter(
                    ({ searchResult, orderNumberSearchResult, searchError }) =>
                        (!!searchResult || !!orderNumberSearchResult) && !searchError
                ),
                map(({ searchTerm }) => searchTerm),
                distinctUntilChanged(arrayEqualsShallow)
            )
            .subscribe(() => this.trackingService.trackEvent(tracking.step.search.successful));

        this.searchStore
            .getSearchTermAndErrorsAndResult()
            .pipe(
                takeUntil(this.destroyed$),
                filter(
                    ({ searchResult, orderNumberSearchResult, searchError }) =>
                        !searchResult &&
                        !orderNumberSearchResult &&
                        !!searchError &&
                        (searchError.orderNumberItemsChecked === undefined ||
                            searchError.orderNumberItemsChecked === true)
                ),
                map(({ searchTerm }) => searchTerm),
                distinctUntilChanged(arrayEqualsShallow)
            )
            .subscribe(() => this.trackingService.trackEvent(tracking.step.search.notSuccessful));
    }
}
